import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdUploadFile, MdWarning, MdCheckCircle } from 'react-icons/md';
import { FaFileExcel } from 'react-icons/fa';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import api from '../../utils/api';
import { useLanguage } from '../../context/LanguageContext';

// "03.09.2026 14:30:00" (tr-TR, Europe/Istanbul local) -> Date
function parseTrDate(s) {
    const m = String(s).trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})[\s,]+(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!m) return null;
    const [, dd, MM, yyyy, hh, mm, ss] = m;
    const iso = `${yyyy}-${MM.padStart(2, '0')}-${dd.padStart(2, '0')}T${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:${(ss || '00').padStart(2, '0')}+03:00`;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
}

function toDate(cell) {
    if (cell == null || cell === '') return null;
    if (cell instanceof Date) return isNaN(cell.getTime()) ? null : cell;
    const str = String(cell).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
        const d = new Date(str);
        if (!isNaN(d.getTime())) return d;
    }
    return parseTrDate(str);
}

const findKey = (obj, candidates) =>
    Object.keys(obj).find(k => {
        const low = k.toLowerCase();
        return candidates.some(c => low.includes(c));
    });

export default function ImportDataModal({ sensorUUID, sensorName, unit, onClose, onSuccess }) {
    const { t } = useLanguage();
    const [preview, setPreview] = useState(null); // { rows, invalid, fileName, from, to }
    const [mode, setMode] = useState('skip');
    const [loading, setLoading] = useState(false);
    const [parsing, setParsing] = useState(false);

    const handleFile = async (file) => {
        if (!file) return;
        setPreview(null);
        setParsing(true);
        try {
            const buf = await file.arrayBuffer();
            const wb = XLSX.read(buf, { type: 'array', cellDates: true });
            const sheet = wb.Sheets[wb.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });

            if (!json.length) {
                toast.error(t('importDataModal.emptyFile'));
                return;
            }

            const sample = json[0];
            const isoKey = findKey(sample, ['iso']);
            const dateKey = isoKey || findKey(sample, ['tarih', 'date', 'zaman', 'time']);
            const valueKey = findKey(sample, ['değer', 'deger', 'value']);

            if (!dateKey || !valueKey) {
                toast.error(t('importDataModal.columnsNotFound'));
                return;
            }

            const rows = [];
            let invalid = 0;
            for (const row of json) {
                const d = toDate(row[dateKey]);
                const v = parseFloat(String(row[valueKey]).replace(',', '.'));
                if (!d || !Number.isFinite(v)) {
                    invalid++;
                    continue;
                }
                rows.push({ timestamp: d.toISOString(), value: v });
            }

            if (!rows.length) {
                toast.error(t('importDataModal.noValidRows'));
                return;
            }

            rows.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            setPreview({
                rows,
                invalid,
                fileName: file.name,
                from: rows[0].timestamp,
                to: rows[rows.length - 1].timestamp
            });
        } catch (err) {
            console.error('Import parse error:', err);
            toast.error(t('importDataModal.parseFailed'));
        } finally {
            setParsing(false);
        }
    };

    const handleImport = async () => {
        if (!preview) return;
        try {
            setLoading(true);
            const res = await api.post(`/sensor-data/${sensorUUID}/import`, {
                rows: preview.rows,
                mode
            });
            const d = res.data;
            toast.success(t('importDataModal.result', {
                inserted: d.inserted ?? 0,
                skipped: d.skipped ?? 0,
                invalid: d.invalid ?? 0
            }));
            onSuccess();
        } catch (err) {
            console.error('Import error:', err);
            toast.error(err.response?.data?.message || t('importDataModal.importFailed'));
        } finally {
            setLoading(false);
        }
    };

    const fmt = (iso) => new Date(iso).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' });

    return (
        <AnimatePresence>
            <div className="modal-overlay" onClick={onClose}>
                <motion.div
                    className="modal"
                    onClick={e => e.stopPropagation()}
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    style={{ maxWidth: 560 }}
                >
                    <div className="modal-header">
                        <div className="modal-title">
                            <div className="modal-title-icon" style={{ background: 'rgba(79, 142, 247, 0.15)', color: 'var(--primary)' }}>
                                <MdUploadFile />
                            </div>
                            {t('importDataModal.title')}
                        </div>
                        <button className="modal-close" onClick={onClose}><MdClose /></button>
                    </div>

                    <div className="modal-body">
                        <div style={{
                            background: 'rgba(245, 158, 11, 0.1)',
                            border: '1px solid rgba(245, 158, 11, 0.2)',
                            padding: '12px 16px',
                            borderRadius: 12,
                            marginBottom: 20,
                            display: 'flex',
                            gap: 12,
                            alignItems: 'flex-start'
                        }}>
                            <MdWarning style={{ color: 'var(--warning)', fontSize: 20, flexShrink: 0, marginTop: 2 }} />
                            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                <span dangerouslySetInnerHTML={{ __html: t('importDataModal.warningDesc', { name: sensorName || '—', unit: unit || '—' }) }} />
                            </div>
                        </div>

                        {!preview && (
                            <label
                                htmlFor="import-file-input"
                                style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                                    padding: '32px 20px', border: '2px dashed var(--border)', borderRadius: 14,
                                    cursor: 'pointer', textAlign: 'center'
                                }}
                            >
                                {parsing
                                    ? <span className="spinner" />
                                    : <FaFileExcel style={{ fontSize: 34, color: '#107c10' }} />}
                                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                                    {parsing ? t('importDataModal.parsing') : t('importDataModal.selectFile')}
                                </div>
                                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('importDataModal.fileHint')}</div>
                                <input
                                    id="import-file-input"
                                    type="file"
                                    accept=".xlsx,.xls"
                                    style={{ display: 'none' }}
                                    onChange={e => handleFile(e.target.files[0])}
                                />
                            </label>
                        )}

                        {preview && (
                            <>
                                <div style={{
                                    background: 'var(--bg-body)', border: '1px solid var(--border)',
                                    borderRadius: 12, padding: '14px 16px', marginBottom: 16, fontSize: 13
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <span style={{ color: 'var(--text-muted)' }}>{t('importDataModal.file')}</span>
                                        <span style={{ fontWeight: 600 }}>{preview.fileName}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <span style={{ color: 'var(--text-muted)' }}>{t('importDataModal.validRows')}</span>
                                        <span style={{ fontWeight: 700, color: 'var(--success)' }}>{preview.rows.length}</span>
                                    </div>
                                    {preview.invalid > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                            <span style={{ color: 'var(--text-muted)' }}>{t('importDataModal.skippedRows')}</span>
                                            <span style={{ fontWeight: 700, color: 'var(--warning)' }}>{preview.invalid}</span>
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'var(--text-muted)' }}>{t('importDataModal.dateRange')}</span>
                                        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmt(preview.from)} → {fmt(preview.to)}</span>
                                    </div>
                                </div>

                                <div style={{ marginBottom: 16 }}>
                                    <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, cursor: 'pointer', border: `1px solid ${mode === 'skip' ? 'var(--primary)' : 'var(--border)'}` }}>
                                        <input type="radio" name="import-mode" checked={mode === 'skip'} onChange={() => setMode('skip')} style={{ marginTop: 3 }} />
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 600 }}>{t('importDataModal.modeSkip')}</div>
                                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('importDataModal.modeSkipDesc')}</div>
                                        </div>
                                    </label>
                                    <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, cursor: 'pointer', marginTop: 8, border: `1px solid ${mode === 'all' ? 'var(--primary)' : 'var(--border)'}` }}>
                                        <input type="radio" name="import-mode" checked={mode === 'all'} onChange={() => setMode('all')} style={{ marginTop: 3 }} />
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 600 }}>{t('importDataModal.modeAll')}</div>
                                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('importDataModal.modeAllDesc')}</div>
                                        </div>
                                    </label>
                                </div>

                                <div style={{ border: '1px solid var(--border)', borderRadius: 12, maxHeight: 180, overflowY: 'auto', marginBottom: 4 }}>
                                    <table className="table" style={{ margin: 0 }}>
                                        <thead>
                                            <tr><th>{t('sensorDetail.dateTime')}</th><th>{t('common.value')}</th></tr>
                                        </thead>
                                        <tbody>
                                            {preview.rows.slice(0, 8).map((r, i) => (
                                                <tr key={i}>
                                                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmt(r.timestamp)}</td>
                                                    <td style={{ fontWeight: 600 }}>{r.value} {unit}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {preview.rows.length > 8 && (
                                    <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                                        {t('importDataModal.andMore', { count: preview.rows.length - 8 })}
                                    </div>
                                )}
                            </>
                        )}

                        <div className="modal-footer" style={{ marginTop: 24, padding: 0, border: 'none' }}>
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                                {t('common.cancel')}
                            </button>
                            {preview && (
                                <button type="button" className="btn btn-secondary" onClick={() => setPreview(null)} disabled={loading}>
                                    {t('importDataModal.chooseAnother')}
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleImport}
                                disabled={loading || !preview}
                                style={{ flex: 1 }}
                            >
                                {loading ? <span className="spinner spinner-sm" /> : <MdCheckCircle />}
                                {t('importDataModal.import')}
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
